import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { getFieldOperationsSettings } from '../services/fieldOperationsService';
import type { FieldOperationsSettings, FieldTargetKind } from '../types/FieldOperations';

type FieldOperationsSettingsContextValue = {
  settings: FieldOperationsSettings;
  isModuleEnabled: boolean;
  allowsTarget: (kind: FieldTargetKind) => boolean;
  loading: boolean;
  refresh: () => Promise<void>;
};

const disabledSettings: FieldOperationsSettings = {
  id: '',
  isEnabled: false,
  allowLocation: true,
  allowAdHoc: false,
  allowCalendarEvent: false,
  allowTask: false,
  mobileGpsRequired: true,
  webGpsRequired: false,
  enforceGeofenceOnCheckIn: true,
  defaultRadiusMeters: 150,
};

const FieldOperationsSettingsContext = createContext<FieldOperationsSettingsContextValue | undefined>(undefined);

export const FieldOperationsSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  const canSeeModule = hasPermission(PermissionScopes.fieldOperationsCheckIn)
    || hasPermission(PermissionScopes.fieldOperationsSettings);
  const [settings, setSettings] = useState<FieldOperationsSettings | null>(null);
  // Starts true so routes and nav do not flash the disabled state before the first fetch.
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !canSeeModule) {
      setSettings(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setSettings(await getFieldOperationsSettings());
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [canSeeModule, isAuthenticated]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void refresh();
  }, [authLoading, refresh]);

  const value = useMemo<FieldOperationsSettingsContextValue>(() => {
    const resolved = settings ?? disabledSettings;
    return {
      settings: resolved,
      isModuleEnabled: resolved.isEnabled,
      allowsTarget: (kind) => {
        if (!resolved.isEnabled) {
          return false;
        }

        switch (kind) {
          case 'location':
            return resolved.allowLocation;
          case 'adHoc':
            return resolved.allowAdHoc;
          case 'calendarEvent':
            return resolved.allowCalendarEvent;
          case 'task':
            return resolved.allowTask;
          default:
            return false;
        }
      },
      loading,
      refresh,
    };
  }, [loading, refresh, settings]);

  return (
    <FieldOperationsSettingsContext.Provider value={value}>
      {children}
    </FieldOperationsSettingsContext.Provider>
  );
};

export const useFieldOperationsSettings = (): FieldOperationsSettingsContextValue => {
  const context = useContext(FieldOperationsSettingsContext);
  if (!context) {
    throw new Error('useFieldOperationsSettings must be used within a FieldOperationsSettingsProvider');
  }
  return context;
};
